import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import downloadHandler from '@lib/utils/downloadHandler';
import useVideoWatchedSeconds from './useVideoWatchedSeconds';
import styles from './VideoViewer.module.scss';

interface VideoSourceUrls {
  video1080p?: string | null;
  video720p?: string | null;
  video480p?: string | null;
  original?: string | null;
}

export interface VideoTrack {
  src: string;
  srcLang: string;
  label: string;
  kind?: 'subtitles' | 'captions';
}

interface Props {
  fileUrl: string;
  downloadUrl: string;
  posterUrl?: string;
  videoUrls?: VideoSourceUrls;
  /**
   * Future-proof slot for caption/subtitle tracks. When empty (default), the
   * player is visually identical to the original native-controls layout.
   * BE does not produce caption tracks today.
   */
  tracks?: VideoTrack[];
  /**
   * Fires on every whole-second change in accrued playback time. Used by
   * FileViewerModal to keep a duration that outlives this component, which
   * unmounts on close.
   */
  onWatchedSecondsChange?: (seconds: number) => void;
  onFullScreenToggle?: () => void;
  isFullScreen?: boolean;
  useDownloadTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
}

export default function VideoViewer(props: Props) {
  const {
    fileUrl,
    downloadUrl,
    posterUrl,
    videoUrls,
    tracks,
    onWatchedSecondsChange,
    onFullScreenToggle,
    isFullScreen,
    useDownloadTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [intrinsicRatio, setIntrinsicRatio] = useState<number | null>(null);

  useVideoWatchedSeconds({ videoRef, onWatchedSecondsChange });

  const handleLoadedMetadata = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const { videoWidth, videoHeight } = event.currentTarget;
      if (videoWidth > 0 && videoHeight > 0) {
        setIntrinsicRatio(videoWidth / videoHeight);
      }
    },
    [],
  );

  const sources = useMemo(() => {
    const candidates: { src: string; type?: string }[] = [];
    if (videoUrls?.video1080p) {
      candidates.push({ src: videoUrls.video1080p, type: 'video/mp4' });
    }
    if (videoUrls?.video720p) {
      candidates.push({ src: videoUrls.video720p, type: 'video/mp4' });
    }
    if (videoUrls?.video480p) {
      candidates.push({ src: videoUrls.video480p, type: 'video/mp4' });
    }
    if (candidates.length === 0 && fileUrl) {
      candidates.push({ src: fileUrl });
    }
    return candidates;
  }, [fileUrl, videoUrls]);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.actionsRow}>
          {onFullScreenToggle && (
            <IconButton
              icon={isFullScreen ? 'fullscreen_exit' : 'fullscreen'}
              onClick={onFullScreenToggle}
              symbolsOutlined
            />
          )}
        </div>
        <div className={styles.actionsRow}>
          {useDownloadTool && (
            <IconButton
              icon="download"
              onClick={() => downloadHandler(downloadUrl)}
              symbolsOutlined
            />
          )}
          {useFavoriteTool && onFavoriteToggle && (
            <IconButton
              icon={isFavorited ? 'bookmark_star' : 'bookmark'}
              onClick={onFavoriteToggle}
              symbolsOutlined
              filled={isFavorited}
              isLoading={isFavoriteLoading}
              type={IconButtonTypes.PrimaryOutlined}
            />
          )}
        </div>
      </div>
      <div className={styles.videoWrap}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className={styles.video}
          controls
          controlsList="nodownload"
          poster={posterUrl}
          preload="metadata"
          playsInline
          onLoadedMetadata={handleLoadedMetadata}
          style={intrinsicRatio ? { aspectRatio: intrinsicRatio } : undefined}
        >
          {sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
          {(tracks || []).map((track) => (
            <track
              key={`${track.kind ?? 'subtitles'}-${track.srcLang}-${track.src}`}
              src={track.src}
              srcLang={track.srcLang}
              label={track.label}
              kind={track.kind ?? 'subtitles'}
            />
          ))}
        </video>
      </div>
    </div>
  );
}
